import { supabase } from "@/lib/supabase";
import { NavigationSettings, DEFAULT_NAVIGATION_SETTINGS } from "@/types/navigationTypes";

export class NavigationService {
  /**
   * Get navigation settings for the current organization
   */
  static async getNavigationSettings(): Promise<NavigationSettings> {
    try {
      const { data, error } = await supabase.rpc('get_navigation_settings');
      
      if (error) {
        console.error('Error fetching navigation settings:', error);
        return DEFAULT_NAVIGATION_SETTINGS;
      }
      
      // Merge with defaults to ensure all keys exist
      return {
        ...DEFAULT_NAVIGATION_SETTINGS,
        ...data
      };
    } catch (error) {
      console.error('Error in getNavigationSettings:', error);
      return DEFAULT_NAVIGATION_SETTINGS;
    }
  }

  /**
   * Update navigation settings (bizadmin only)
   */
  static async updateNavigationSettings(settings: NavigationSettings): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('update_navigation_settings', {
        settings
      });
      
      if (error) {
        console.error('Error updating navigation settings:', error);
        throw new Error(error.message);
      }
      
      return data === true;
    } catch (error) {
      console.error('Error in updateNavigationSettings:', error);
      throw error;
    }
  }

  /**
   * Check if user can manage navigation settings
   */
  static async canManageNavigation(): Promise<boolean> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user?.user) {
        console.log('NavigationService: No authenticated user found');
        return false;
      }

      console.log('NavigationService: Checking permissions for user:', user.user.id);

      const { data, error } = await supabase
        .from('user_profiles')
        .select('role, can_manage_navigation')
        .eq('id', user.user.id)  // Using 'id' not 'user_id' based on your table structure
        .single();

      if (error) {
        console.error('NavigationService: Error checking navigation permissions:', error);
        return false;
      }

      console.log('NavigationService: User profile data:', data);

      // Bizadmins always have permission, or explicit permission granted
      const hasPermission = data?.role === 'bizadmin' || data?.can_manage_navigation === true;
      console.log('NavigationService: Has navigation permission:', hasPermission);
      
      return hasPermission;
    } catch (error) {
      console.error('NavigationService: Error in canManageNavigation:', error);
      return false;
    }
  }

  /**
   * Grant/revoke navigation management permission to a user
   */
  static async setNavigationPermission(userId: string, canManage: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ can_manage_navigation: canManage })
        .eq('user_id', userId);

      if (error) {
        console.error('Error setting navigation permission:', error);
        throw new Error(error.message);
      }

      return true;
    } catch (error) {
      console.error('Error in setNavigationPermission:', error);
      throw error;
    }
  }
}